import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, ShoppingBag, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import SEO from "@/components/SEO";

/**
 * Public proof approval + checkout page. URL: /preview/:sessionId
 *
 * Flow:
 *   1. Customer clicks the link in their "proof ready" email
 *   2. Sees watermarked hand-crafted proof
 *   3. Clicks "Looks great — Buy Now"
 *   4. We approve the session + add the parent Shopify product to cart with
 *      the personalisation session ID as a line-item attribute
 *   5. Cart drawer opens; customer goes through normal Shopify checkout
 */
export default function PersonalisationSharePreview() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get(`/personalisation/${sessionId}`)
      .then((r) => {
        setSession(r.data);
        return api.get(`/products/${r.data.shopify_product_id}`);
      })
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [sessionId]);

  const approveAndBuy = async () => {
    if (!session || !product) return;
    setBusy(true);
    try {
      // Approve the session if not already approved
      if (session.status !== "approved") {
        const r = await api.post(`/personalisation/${session.id}/approve`);
        setSession(r.data);
      }
      // Find the first available variant (customer can choose size in cart)
      const variant = (product.variants || [])[0];
      if (!variant) {
        toast.error("This product is out of stock right now.");
        setBusy(false);
        return;
      }
      await addItem({
        variantId: variant.id,
        quantity: 1,
        attributes: [
          { key: "Personalisation", value: "Custom photo upload" },
          { key: "_wc_personalisation_id", value: session.id },
        ],
      });
      toast.success("Added! Heading to checkout…", { duration: 2000 });
    } catch (e) {
      const msg =
        e?.response?.data?.detail || "Couldn't add to cart. Try again?";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-[800px] px-6 py-24 text-center md:px-10 md:py-32">
        <SEO title="Preview not found" path={`/preview/${sessionId}`} />
        <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight md:text-5xl">
          That preview vanished into the chaos.
        </h1>
        <p className="mt-4 text-base text-[#1A3626]/75 md:text-lg">
          Try again from a fresh personalisation session.
        </p>
        <Link
          to="/shop"
          className="wc-btn mt-10 inline-flex items-center gap-2"
          data-testid="share-back-to-shop"
        >
          Browse the shop
          <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </Link>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-[800px] px-6 py-24 md:px-10">
        <div className="aspect-square w-full animate-pulse bg-[#EBE7DD]" />
      </div>
    );
  }

  const isApproved = session.status === "approved";
  const isReady = session.status === "proof_ready" || isApproved;

  return (
    <div className="bg-[#F9F9F4] text-[#1A3626]" data-testid="share-preview-page">
      <SEO
        title="Your Wet Cabbage proof"
        path={`/preview/${sessionId}`}
        description="Your hand-crafted personalised proof from Wet Cabbage."
        image={session.preview_url ? fileUrl(session.preview_url) : undefined}
      />
      <section className="mx-auto max-w-[860px] px-6 pb-24 pt-16 md:px-10 md:pb-32 md:pt-24">
        <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
          {isApproved ? "Approved · Ready to print" : "Your hand-crafted proof"}
        </div>
        <h1
          className="font-display mt-3 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl"
          data-testid="share-heading"
        >
          {product?.name || "Your Wet Cabbage proof"}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#1A3626]/80 md:text-[15px]">
          Watermark removed on the printed tee. If it&rsquo;s not right just
          reply to your email and we&rsquo;ll redo it — no charge until you
          approve.
        </p>

        {!isReady && (
          <div
            className="mt-6 flex items-start gap-2 border-2 border-[#1A3626] bg-[#EBE7DD] px-4 py-3 text-sm"
            data-testid="not-ready-banner"
          >
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0"
              strokeWidth={2.5}
            />
            <span>
              Your proof is still being hand-crafted. We&rsquo;ll email you as
              soon as it&rsquo;s ready.
            </span>
          </div>
        )}

        <div className="relative mt-8 select-none">
          {session.preview_url ? (
            <img
              src={fileUrl(session.preview_url)}
              alt="Your Wet Cabbage proof"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              className="block w-full select-none border-2 border-[#1A3626] bg-black object-contain"
              style={{
                WebkitUserSelect: "none",
                userSelect: "none",
                WebkitTouchCallout: "none",
              }}
              data-testid="share-preview-image"
            />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center border-2 border-[#1A3626] bg-[#EBE7DD] text-sm uppercase tracking-[0.3em]">
              Proof being prepared
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#1A3626]/60">
          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
          Preview only · high-resolution print artwork delivered after payment
        </div>

        {isReady && (
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              onClick={approveAndBuy}
              disabled={busy}
              className="wc-btn flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="approve-and-buy-cta"
            >
              {isApproved ? (
                <CheckCircle2 className="h-4 w-4" strokeWidth={2.8} />
              ) : (
                <Sparkles className="h-4 w-4" strokeWidth={2.8} />
              )}
              {busy
                ? "Adding…"
                : isApproved
                ? "Buy Now — Add to Cart"
                : "Looks Great — Buy Now"}
            </button>
            <Link
              to={`/shop/${session.shopify_product_id}`}
              className="font-display flex items-center justify-center gap-2 border-2 border-[#1A3626] bg-transparent px-7 py-3.5 text-xs font-black uppercase tracking-[0.32em] text-[#1A3626] transition-all hover:bg-[#1A3626] hover:text-[#F9F9F4]"
              data-testid="share-back-to-product"
            >
              <ShoppingBag className="h-4 w-4" strokeWidth={2.6} />
              See product details
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
